import { useMemo } from "react";

import constant from "@/config/constant";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Account-scoped feature flags.
 *
 * The allow-lists live in `constant.ts` so turning a feature on for another
 * customer is a one-line change with no other code to read. `loggedEmail` is
 * the identity they are matched against: it is written at login (`Login.tsx`)
 * and cleared at logout (`AuthContext.logout`), which makes it the most
 * reliable per-user anchor available on the client — the same one the query
 * cache keys on (see `useUserKey`).
 */

/** The signed-in email, lowercased. Empty when there is no session. */
function currentEmail(): string {
  if (typeof window === "undefined") return "";
  return (window.localStorage.getItem("loggedEmail") || "").trim().toLowerCase();
}

/**
 * True when `email` is covered by `accounts`. An entry matches either the whole
 * address or just the local part, so a list can grant a single mailbox
 * ("someone@example.com") or an account name across domains ("vna.bill.demo").
 */
export function accountAllowed(accounts: string[], email: string): boolean {
  if (!email) return false;
  const local = email.split("@")[0];
  return accounts.some((entry) => {
    const want = entry.trim().toLowerCase();
    if (!want) return false;
    return want === email || want === local;
  });
}

/** Whether the Charlie (Administrator Daily Brief) feature is on for this user. */
export function isCharlieEnabled(): boolean {
  return accountAllowed(constant.charlieAccounts, currentEmail());
}

/**
 * Hook form of `isCharlieEnabled`, for the nav and the route guard.
 *
 * `user` is the recompute trigger: `setAuth` and `logout` both change it, and
 * `loggedEmail` is written in the same handler as `setAuth`, so it is already
 * in localStorage by the time the resulting render runs this.
 */
export function useCharlieEnabled(): boolean {
  const { user } = useAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => isCharlieEnabled(), [user]);
}
